import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PriceInput } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { getAmountInputErrorMessage, getAmountSumInputErrorMessage } from './utils/getErrorMessage';
import { WrapToError } from './ContributionEmployeesSettings.styles';
import { ContributionAmountSideKey } from './ContributionEmployeesSettings.types';
import {useRecipientsStore} from './stores';
import { ContributionAccount, ContributionBackendErrors, Recipient } from './types';
import WarnIconWithText from './WarnIconWithText';

interface Props {
  amountKey: ContributionAmountSideKey;
  recipient: Recipient;
  contributionAccount: ContributionAccount;
  refInput?: React.RefObject<HTMLInputElement | null>;
  areErrorsShown?: boolean;
  backendError?: ContributionBackendErrors;
}

const AmountInput: React.FC<Props> = ({
  recipient,
  contributionAccount,
  amountKey,
  refInput,
  areErrorsShown,
  backendError,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const { recipients, setRecipients } = useRecipientsStore();
  const errorMessage = useMemo(
    () => getAmountInputErrorMessage(contributionAccount, areErrorsShown, backendError),
    [areErrorsShown, contributionAccount, backendError]
  );
  const sumErrorMessage = useMemo(
    () => getAmountSumInputErrorMessage(contributionAccount, backendError),
    [contributionAccount, backendError]
  );

  useEffect(() => () => setIsFocused(false), []);

  const handleChange = useCallback(
    (val: string) => {
      if (isFocused) {
        setRecipients(
          recipients?.map((item) => {
            if (item?.employeeId === recipient?.employeeId) {
              return {
                ...item,
                contributionAccounts: item?.contributionAccounts?.map((acc) => {
                  if (acc?.planId === contributionAccount?.planId) {
                    const updatedAcc = {
                      ...contributionAccount,
                      [amountKey]: val,
                    };

                    // return disableAccountIfZeroAmounts(updatedAcc);
                    return updatedAcc;
                  }
                  return acc;
                }),
              };
            }
            return item;
          })
        );
      }
    },
    [amountKey, contributionAccount, isFocused, recipient?.employeeId, recipients, setRecipients]
  );

  return (
      <WrapToError
        hasError={Boolean(errorMessage || sumErrorMessage)}
        width={{ max: '140px' }}
        onBlur={() => setIsFocused(false)}
        onFocus={() => setIsFocused(true)}
      >
        <PriceInput
          testId={`AmountInput_${amountKey}`}
          disabled={contributionAccount?.isDisabled}
          name={amountKey}
          value={contributionAccount?.isDisabled ? '' : toString(contributionAccount?.[amountKey])}
          onChange={handleChange}
          placeholder="Set amount"
          refInput={refInput}
          allowNegativeValue
          {...(errorMessage || sumErrorMessage
            ? {
                rightIcon: <WarnIconWithText
                  message={errorMessage || sumErrorMessage}
                  hideStyle={{
                    ...ContributionAmountSideKey.EmployeeContributionAmount === amountKey && !!sumErrorMessage ? { marginLeft: '70px', marginBottom: '12px' } : {},
                    ...ContributionAmountSideKey.EmployerContributionAmount === amountKey && !!sumErrorMessage ? { marginRight: '241px', marginBottom: '12px' } : {},
                  }}
                />,
              }
            : {})}
        />
      </WrapToError>
  );
};

export default AmountInput;
